"use client";

import type { CSSProperties } from "react";

import { collectPhiSelectedNavKeys } from "../../../../../helpers/nav-selection";
import { PhiMenuControl } from "../../../../../components/controls/phi-menu-control";
import type { PhiClientBlockBaseProps, PhiBlockRuntime, PhiNoLabels } from "../../../../../types";
import { usePhiConfig } from "../../../../../components/root/phi-config-provider";
import { mapPhiNavItems } from "../../../../../components/shell/menu-items";
import { type PhiMenuTheme, type PhiNavItem } from "../../../../../components/shell/shell-types";
import { usePhiPathname } from "../../../../../components/runtime/use-phi-pathname";

export type PhiHeaderNavigationWidgetClientProps = PhiClientBlockBaseProps<
  PhiNoLabels,
  Record<string, never>,
  Pick<PhiBlockRuntime, "site" | "locale" | "area">
> & {
  // Always handed over with the request's locale, so no reader has a language to invent.
  runtime: Pick<PhiBlockRuntime, "site" | "locale" | "area">;
  items: PhiNavItem[];
  menuTheme?: PhiMenuTheme;
  height?: CSSProperties["height"];
  align?: "left" | "center";
  interactive?: boolean;
};

export function PhiHeaderNavigationWidgetClient({
  runtime,
  items,
  menuTheme,
  height,
  align = "left",
  interactive = true,
}: PhiHeaderNavigationWidgetClientProps) {
  const { token } = usePhiConfig();
  const pathname = usePhiPathname();
  const availableLocales = runtime.site.availableLocales.map((option) => option.code);
  const selectedKeys = collectPhiSelectedNavKeys(pathname, items, availableLocales);
  const menuItems = mapPhiNavItems(
    runtime.locale.current,
    runtime.area,
    pathname,
    items,
    availableLocales,
    { interactive },
  );
  const resolvedHeight =
    typeof height === "number" ? `${height}px` : (height ?? "100%");

  if (menuItems.length === 0) {
    return null;
  }

  if (!interactive) {
    return (
      <nav
        aria-label="Header navigation preview"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: align === "center" ? "center" : "flex-start",
          gap: 24,
          width: "100%",
          height: "100%",
          minWidth: 0,
          minHeight: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontSize: token.fontSizeLG,
        }}
      >
        {items.map((item) => (
          <span
            key={item.key}
            style={{
              display: "inline-flex",
              alignItems: "center",
              minWidth: 0,
              color: "inherit",
              textDecoration: "none",
            }}
          >
            {item.label}
          </span>
        ))}
      </nav>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        justifyContent: align === "center" ? "center" : "flex-start",
      }}
    >
      <PhiMenuControl
        mode="horizontal"
        menuTheme={menuTheme}
        selectedKeys={selectedKeys}
        items={menuItems}
        fontSize={token.fontSizeLG}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: align === "center" ? "center" : "flex-start",
          minHeight: 0,
          flex: "1 1 auto",
          paddingInline: 0,
          height: resolvedHeight,
          lineHeight: resolvedHeight,
        } as CSSProperties}
      />
    </div>
  );
}
